import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { processStreamToken } from "./utils/streamProcessor";
import { ChatStatus, StreamPhase, ProcessingNode, ToolCallStatus } from "@/constants/enums";

export type ToolCallState = {
  id: string;
  toolName: string;
  status: ToolCallStatus;
};

type DocumentChatRuntimeState = {
  status: ChatStatus;
  phase: StreamPhase;
  currentNode: ProcessingNode;
  processingStagesComplete: boolean;
  text: string;
  thinkingText: string;
  toolCalls: ToolCallState[];
  error: string | null;
  sourcesFromMetadata: any[] | null;
  streamingContent: string;
  rawStreamBuffer: string;
  thinkingStartAt: number | null;
  answerStartAt: number | null;
};

type DocumentChatRuntimeActions = {
  // Generic update for simple state changes
  update: (_updates: Partial<DocumentChatRuntimeState>) => void;

  // Specialized actions that need logic
  appendThinking: (_chunk: string) => void;
  addToolCall: (_id: string, _toolName: string) => void;
  finishToolCall: (_id: string) => void;
  appendStreamingToken: (_token: string) => void;
  clearStreamingContent: () => void;
  markThinkingStart: () => void;
  markAnswerStart: () => void;

  // Reset
  resetRuntime: () => void;
};

const initialState: DocumentChatRuntimeState = {
  status: ChatStatus.IDLE,
  phase: StreamPhase.IDLE,
  currentNode: ProcessingNode.IDLE,
  processingStagesComplete: false,
  text: "",
  thinkingText: "",
  toolCalls: [],
  error: null,
  sourcesFromMetadata: null,
  streamingContent: "",
  rawStreamBuffer: "",
  thinkingStartAt: null,
  answerStartAt: null,
};

export const useDocumentChatRuntime = create<
  DocumentChatRuntimeState & DocumentChatRuntimeActions
>()(
  persist(
    (set) => ({
      ...initialState,

      // Generic update action for simple state changes
      update: (updates) => set(updates),

      // Specialized actions
      appendThinking: (chunk) => set((state) => ({ thinkingText: state.thinkingText + chunk })),

      addToolCall: (id, toolName) =>
        set((state) => {
          if (state.toolCalls.some((t) => t.id === id)) return state;
          return { toolCalls: [...state.toolCalls, { id, toolName, status: ToolCallStatus.RUNNING }] };
        }),

      finishToolCall: (id) =>
        set((state) => ({
          toolCalls: state.toolCalls.map((t) => (t.id === id ? { ...t, status: ToolCallStatus.DONE } : t)),
        })),

      markThinkingStart: () => set((s) => ({ thinkingStartAt: s.thinkingStartAt ?? Date.now() })),
      markAnswerStart: () => set((s) => ({ answerStartAt: s.answerStartAt ?? Date.now() })),

      // Streaming content
      appendStreamingToken: (token) =>
        set((state) => {
          const result = processStreamToken(state.rawStreamBuffer, token, state.sourcesFromMetadata);
          return {
            rawStreamBuffer: result.rawStreamBuffer,
            streamingContent: result.streamingContent,
            sourcesFromMetadata: result.sourcesFromMetadata,
          };
        }),

      clearStreamingContent: () =>
        set({
          streamingContent: "",
          rawStreamBuffer: "",
        }),

      resetRuntime: () => set(initialState),
    }),
    {
      name: "document-chat-runtime",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
